import { flash, Tabs } from "@xola/ui-kit";
import React from "react";
import { useHistory, useParams } from "react-router-dom";
import { Affiliate, Permission } from "types";
import { useUser } from "../../hooks/useAccount";
import { useAuthorization } from "../../hooks/useAuthorization";
import { useCollection } from "../../hooks/useCollection";
import { isAffiliate } from "../../models/User";
import { Loader } from "../Loader";
import { AgentPurchaseSettings } from "./AgentPurchaseSettings";
import { CommercialTerms } from "./CommercialTerms";
import { EditAgentForm } from "./EditAgentForm";

interface EditAgentProps {
    agent?: Affiliate;
    onSave: (agentId?: ID) => void;
}

const tabs = {
    agentProfile: "Agent Profile",
    commercialTerms: "Commercial Terms",
    purchaseSettings: "Purchase Settings",
};

const tabKeys = Object.keys(tabs);

export const EditAgent = ({ agent, onSave }: EditAgentProps) => {
    const { tab = "agentProfile" } = useParams<{ tab?: string }>();
    const user = useUser();
    const history = useHistory();
    const existingAgent = !!agent?.id;
    const { isGranted } = useAuthorization();

    const {
        data: [loggedInAffiliate],
    } = useCollection<Affiliate>(
        "affiliates",
        { user: user.id, limit: 1, status: "active" },
        { enabled: isAffiliate(user) },
    );
    // Restrict access for logged in affiliate
    const allowEdit = isAffiliate(user)
        ? agent?.user?.id === user.id || (agent?.parent === loggedInAffiliate?.id && loggedInAffiliate?.allowChildren)
        : true;
    const allowCreate = isAffiliate(user) ? !loggedInAffiliate?.parent && loggedInAffiliate?.allowChildren : true;

    const changeTab = (tabIndex: number) => {
        if (agent) {
            history.push(`/distribution/agents/${agent.id}/${tabKeys[tabIndex]}`);
        }
    };

    const handleSubmitAgentForm = (agent: Affiliate) => {
        flash.success(`Agent successfully ${existingAgent ? "updated" : "created"}`);

        if (existingAgent) {
            return;
        }

        if (isGranted(Permission.PARTNERS_COMMERCIAL_TERMS_VIEW)) {
            history.push(`/distribution/agents/${agent.id}/commercialTerms`);
        } else {
            history.push("/distribution/agents");
        }
    };

    if (agent) {
        // edit workflow
        if (!allowEdit) {
            return null;
        }
    } else if (!allowCreate) {
        return null;
    }

    return (
        <Tabs value={tabKeys.indexOf(tab)} onChange={changeTab}>
            <Tabs.Tab>{tabs.agentProfile}</Tabs.Tab>
            <Tabs.Panel>
                <EditAgentForm agent={agent} onSubmit={handleSubmitAgentForm} />
            </Tabs.Panel>

            <Tabs.Tab isHidden={!isGranted(Permission.PARTNERS_COMMERCIAL_TERMS_VIEW)} disabled={!agent}>
                {tabs.commercialTerms}{" "}
            </Tabs.Tab>
            <Tabs.Panel>{agent ? <CommercialTerms agent={agent} /> : <Loader />}</Tabs.Panel>

            <Tabs.Tab isHidden={!isGranted(Permission.PARTNERS_PURCHASE_SETTINGS_VIEW)} disabled={!agent}>
                {tabs.purchaseSettings}
            </Tabs.Tab>
            <Tabs.Panel>{agent ? <AgentPurchaseSettings agent={agent} onSave={onSave} /> : <Loader />}</Tabs.Panel>
        </Tabs>
    );
};
